import React, { useEffect, useMemo, useState } from "react";
import { API_URL, authFetch, parseJsonResponse, responseErrorMessage } from "../../utils/auth";
import ToolSidebar from "../../utils/ToolSideBar";

import AccountCard from "./AccountCard";
import StripeCard from "./StripeCard";
import PayoutRulesCard from "./PayoutRulesCard";
import NotificationsCard from "./NotificationsCard";
import AvailabilitySettingsCard from "./AvailabilitySettingsCard";
import SecurityCard from "./SecurityCard";
import WelcomeMessageCard from "./WelcomeMessageCard";
import SuperuserSettingsPage from "./SuperuserSettingsPage";
import { features } from "../../utils/features";

import "../../../css/partner/PartnerSettings.css";

function getPortalRole() {
  const role = localStorage.getItem("portal_role");
  if (role) return role;
  if (localStorage.getItem("is_partner") === "true") return "partner";
  if (localStorage.getItem("is_client") === "true") return "client";
  return "client";
}

function formatProjectAccessStatus(project) {
  const value = project.status_label || project.status || project.payment_state || "Active";
  return String(value).replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function ClientProjectAccessCard({ projects = [] }) {
  return (
    <div className="settings-card project-access-card">
      <h3>Project Access</h3>
      {projects.length > 0 ? (
        <div className="project-access-list">
          {projects.slice(0, 4).map((project) => (
            <div className="project-access-row" key={project.id || project.project_id || project.project_name}>
              <span>{project.project_name || project.business_name || "Project"}</span>
              <strong>{formatProjectAccessStatus(project)}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="hint">No active client projects are linked to this account yet.</p>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [data, setData] = useState({ user: {}, partner: null, projects: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const portalRole = useMemo(() => getPortalRole(), []);
  const isSuperuser = localStorage.getItem("is_superuser") === "true" || portalRole === "superuser";
  const isPartner = portalRole === "partner" && !isSuperuser;
  useEffect(() => {
    if (isSuperuser) return undefined;
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      const path = isPartner ? "/partner/profile-overview/" : "/client/profile-overview/";

      try {
        const res = await authFetch(`${API_URL}${path}`);
        const json = await parseJsonResponse(res);
        if (!res.ok) {
          throw new Error(responseErrorMessage(res, json, "Unable to load settings."));
        }
        if (!json) {
          throw new Error("Unable to load settings. The server returned an unexpected response.");
        }
        if (active) {
          setData({ user: json.user || {}, partner: json.partner || null, projects: json.projects || [] });
        }
      } catch (err) {
        console.error(err);
        if (active) setError(err.message || "Unable to load settings.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [isPartner, isSuperuser]);

  if (isSuperuser) {
    return (
      <div className="settings-shell">
        <ToolSidebar active="settings" />
        <main className="settings-main">
          <SuperuserSettingsPage />
        </main>
      </div>
    );
  }

  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-shell">
      <ToolSidebar active="settings" />

      <main className="settings-main">
        <div className="settings-header">
          <span>Settings</span>
          <h1>Workspace preferences</h1>
          <p>{isPartner ? "Manage account details, payouts, notifications, and security." : "Manage account details, project access, notifications, and security."}</p>
        </div>

        {error && <div className="settings-error">{error}</div>}

        <div className="settings-grid">
          <AccountCard user={data.user} />
          {isPartner ? (
            <>
              <StripeCard partner={data.partner} />
              <PayoutRulesCard partner={data.partner} onUpdated={(partner) => setData((current) => ({ ...current, partner }))} />
              <NotificationsCard />
              {features.meetingsEnabled && (
                <div className="settings-wide">
                  <AvailabilitySettingsCard compact />
                </div>
              )}
              {data.partner && <WelcomeMessageCard />}
            </>
          ) : (
            <>
              <ClientProjectAccessCard projects={data.projects} />
              <NotificationsCard />
            </>
          )}
          <SecurityCard />
        </div>
      </main>
    </div>
  );
}



